import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCoachCommissionTables, patchCoachCommissionTables, putCoachExceptionRates } from './api';
import { coachCommissionTableKeys } from './keys';
import { ExceptionSaveFormat, PatchCoachCommissionTableBody, PutCoachExceptionRatesBody } from './types';
import { sessionReportKeys } from '../sessionReport/keys';

export const useCoachCommissionTables = ({
    centerId,
    year,
    month,
    enabled = true,
}: {
    centerId: string;
    year: string;
    month: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: coachCommissionTableKeys.list(centerId, year, month),
        queryFn: () => getCoachCommissionTables({ centerId, year, month }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });

export const usePatchCoachCommissionTables = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            body,
        }: {
            centerId: string;
            body: PatchCoachCommissionTableBody;
        }) => patchCoachCommissionTables({ centerId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: coachCommissionTableKeys.list(
                    variables.centerId,
                    variables.body.year,
                    variables.body.month,
                ),
            });
            // 수수료(고정/비율) 변경 시 급여계산·수업료 정산 결과가 바뀌므로 함께 무효화한다.
            // (호출부 ModalCommission 은 getSalaryCalcsQuery 를 무효화하지 않아 급여계산 화면이 stale 해진다)
            queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] });
            queryClient.invalidateQueries({
                queryKey: ['getClassFeeSettlementForMembershipQuery'],
            });
        },
    });
};

/**
 * 예외 수업료 저장.
 *
 * 성공해도 요율 목록을 **다시 읽지 않는다**(refetchType: 'none'). 요율 탭은 서버 응답이 바뀌면
 * 편집 중인 구간 요율을 서버 값으로 되돌리는데, 예외만 저장했는데 아직 「적용하기」 전인
 * 구간 편집이 사라지면 안 되기 때문이다. 대신 낡음 표시만 해 두어 다음에 열 때 새로 읽는다.
 * 화면 표시는 호출부가 응답값으로 직접 갱신한다.
 */
export const useSaveCoachExceptionRates = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            body,
            format,
        }: {
            centerId: string;
            body: PutCoachExceptionRatesBody;
            format?: ExceptionSaveFormat;
        }) => putCoachExceptionRates({ centerId, body, format }),
        onSuccess: () => {
            // 이번 달뿐 아니라 이후 달도 이 값을 이어받을 수 있어 요율 목록 전체를 낡음 처리한다.
            queryClient.invalidateQueries({ queryKey: coachCommissionTableKeys.all, refetchType: 'none' });
            queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] });
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] });
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] });
            // 매출 캘린더의 일별 수업료도 예외 수업료로 다시 계산된다(F7). 캘린더는 편집 상태가 없어 바로 다시 읽어도 된다.
            queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] });
            // 세션 현황의 진행 IN/OUT 도 「시간 상관없이 IN」 예외로 다시 판정된다(서버가 같은 판정을 쓴다).
            queryClient.invalidateQueries({ queryKey: sessionReportKeys.all });
        },
    });
};
